import { AddTagFormData, ItemFilters } from '../types';
import { transformFormDataToAPI } from './transformers';
import { uploadImageIfNeeded } from './imageUtils';
import { SampleTagCreateData } from '../../../../Lib/Api/sample-tags';

const SELF_FIELDS = ['fiberContents', 'fabricWeight', 'fabricCodeName'];
const SAMPLE_CREATED_WITH_FIELDS = [
  'fabricType',
  'trimType',
  'labelType',
  'washColorType',
];

/**
 * Detects changes between current form data and initial form data
 */
export const detectFormChanges = (
  currentData: AddTagFormData,
  initialData: AddTagFormData | null,
): Partial<AddTagFormData> => {
  if (!initialData) return {};

  const changes: Record<string, unknown> = {};
  const dataEntries = Object.entries(currentData);
  const initialEntries = Object.entries(initialData);
  const initialMap = new Map(initialEntries);

  dataEntries.forEach(([key, currentValue]) => {
    const initialValue = initialMap.get(key);
    if (JSON.stringify(currentValue) !== JSON.stringify(initialValue)) {
      changes[key] = currentValue;
    }
  });

  return changes as Partial<AddTagFormData>;
};

/**
 * Adds required fields for grouped updates (self section, sample created with)
 */
export const addRequiredFieldsForGroups = (
  changes: Partial<AddTagFormData>,
  formData: AddTagFormData,
): Partial<AddTagFormData> & { sampleType: ItemFilters } => {
  const updatesWithRequiredFields: Partial<AddTagFormData> & {
    sampleType: ItemFilters;
  } = {
    ...changes,
    sampleType: formData.sampleType,
  };

  if (SELF_FIELDS.some((field) => field in changes)) {
    updatesWithRequiredFields.fiberContents = formData.fiberContents;
    updatesWithRequiredFields.fabricWeight = formData.fabricWeight;
    updatesWithRequiredFields.fabricCodeName = formData.fabricCodeName;
  }

  if (SAMPLE_CREATED_WITH_FIELDS.some((field) => field in changes)) {
    updatesWithRequiredFields.fabricType = formData.fabricType;
    updatesWithRequiredFields.trimType = formData.trimType;
    updatesWithRequiredFields.labelType = formData.labelType;
    updatesWithRequiredFields.washColorType = formData.washColorType;
  }

  return updatesWithRequiredFields;
};

/**
 * Processes form data with image upload
 */
export const processFormDataWithImage = async (
  data: AddTagFormData,
  userId: string,
): Promise<AddTagFormData> => {
  const uploadedImageUrl = await uploadImageIfNeeded(
    data.productImageFile,
    userId,
  );

  return {
    ...data,
    productImageUrl: uploadedImageUrl || data.productImageUrl,
    productImageFile: undefined,
  };
};

/**
 * Handles edit submission for Proto Development (sends entire object)
 */
export const handleProtoEditSubmission = async (
  formData: AddTagFormData,
  userId: string,
  sourceTagData: { id: string },
  updateTag: (params: { tagId: string; updates: SampleTagCreateData }) => void,
) => {
  if (!userId || !sourceTagData) return;

  const processedData = await processFormDataWithImage(formData, userId);
  const apiData = transformFormDataToAPI(processedData);
  updateTag({ tagId: sourceTagData.id, updates: apiData });
};

/**
 * Handles edit submission for other sample types (sends only changes)
 */
export const handleStandardEditSubmission = async (
  formData: AddTagFormData,
  initialFormData: AddTagFormData | null,
  userId: string,
  sourceTagData: { id: string },
  updateTag: (params: { tagId: string; updates: SampleTagCreateData }) => void,
  handleClose: () => void,
) => {
  if (!userId || !sourceTagData) return;

  const changes = detectFormChanges(formData, initialFormData);

  if (Object.keys(changes).length === 0) {
    handleClose();
    return;
  }

  const updatesWithRequiredFields = addRequiredFieldsForGroups(
    changes,
    formData,
  );
  const processedData = await processFormDataWithImage(
    updatesWithRequiredFields as AddTagFormData,
    userId,
  );
  const apiData = transformFormDataToAPI(processedData);
  updateTag({ tagId: sourceTagData.id, updates: apiData });
};

/**
 * Handles create submission
 */
export const handleCreateSubmission = async (
  formData: AddTagFormData,
  userId: string,
  createTag: (apiData: SampleTagCreateData) => void,
) => {
  if (!userId) return;

  const processedData = await processFormDataWithImage(formData, userId);
  const apiData = transformFormDataToAPI(processedData);
  createTag(apiData);
};
